import React, { useState, useEffect, useMemo, useRef } from 'react'
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { t } from '../../components/i18n/i18n'
import back from '../../assets/images/elements/books.jpeg'
import '../../assets/css/pages.css'

// ── Category metadata ─────────────────────────────────────────────────────────

export const CATEGORY_META = {
  sermons:     { labelUk: 'Проповіді',       color: '#c0622a' },
  bible_study: { labelUk: 'Вивчення Біблії', color: '#b58a00' },
  events:      { labelUk: 'Події',           color: '#3a8fd9' },
  choir:       { labelUk: 'Хор',             color: '#7c5cbf' },
  streams:     { labelUk: 'Трансляції',      color: '#3aaa6e' },
}

const CATEGORIES = Object.entries(CATEGORY_META).map(([key, meta]) => ({ key, ...meta }))

const LEGACY_CATEGORY_MAP = {
  sermon: 'sermons',
  word:   'bible_study',
  online: 'streams',
  choir:  'choir',
  events: 'events',
}

const extractVideoId = (link = '') => {
  const m = link.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : ''
}

// ── Date formatter ────────────────────────────────────────────────────────────

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch {
    return ''
  }
}

// ── VideoCard ─────────────────────────────────────────────────────────────────

const VideoCard = ({ video, speakerNames }) => {
  const [thumbSrc, setThumbSrc] = useState(
    video.thumbnail ||
    (video.videoId
      ? `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`
      : null)
  )

  const catMeta = CATEGORY_META[video.category] || null
  const dateStr = formatDate(video.date)

  const handleClick = () => {
    if (video.link) window.open(video.link, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      className={`video-card${video.link ? '' : ' video-card--no-link'}`}
      onClick={handleClick}
    >
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt={video.titleUk || ''}
          onError={() =>
            video.videoId
              ? setThumbSrc(`https://img.youtube.com/vi/${video.videoId}/default.jpg`)
              : setThumbSrc(null)
          }
          className='video-card__thumb'
        />
      ) : (
        <div className='video-card__placeholder'>
          <span>▶</span>
        </div>
      )}

      <div className='video-card__body'>
        {catMeta && (
          <span
            className='video-card__badge'
            style={{ background: catMeta.color }}
          >
            {catMeta.labelUk}
          </span>
        )}

        <div className='video-card__title'>
          {video.titleUk || video.titleEn || ''}
        </div>

        {video.bible && (
          <div className='video-card__bible'>{video.bible}</div>
        )}

        {(speakerNames.length > 0 || video.speakerNote || video._authorLegacy) && (
          <div className='video-card__speaker'>
            {speakerNames.length > 0
              ? speakerNames.join(', ')
              : (video.speakerNote || video._authorLegacy)}
          </div>
        )}

        {dateStr && (
          <div className='video-card__date'>{dateStr}</div>
        )}
      </div>
    </div>
  )
}

// ── ArchiveHero ───────────────────────────────────────────────────────────────

const ArchiveHero = () => (
  <div className='arch-hero'>
    <img src={back} alt='' />
    <div className='arch-hero__overlay' />
    <h1 className='arch-hero__title'>
      {(() => { const v = t('archiveHeader'); return v === 'archiveHeader' ? 'Відеоархів' : v })()}
    </h1>
  </div>
)

// ── CategoryPills ─────────────────────────────────────────────────────────────

const CategoryPills = ({ activeCategory, onToggle }) => (
  <div className='filter-pills'>
    <button
      onClick={() => onToggle('')}
      className='filter-pill'
      style={{
        borderColor: '#c0622a',
        background: activeCategory === '' ? '#c0622a' : '#f5ece0',
        color: activeCategory === '' ? '#fff' : '#5c3d2e',
      }}
    >
      Всі
    </button>
    {CATEGORIES.map(cat => (
      <button
        key={cat.key}
        onClick={() => onToggle(cat.key)}
        className='filter-pill'
        style={{
          borderColor: cat.color,
          background: activeCategory === cat.key ? cat.color : '#f5ece0',
          color: activeCategory === cat.key ? '#fff' : '#5c3d2e',
        }}
      >
        {cat.labelUk}
      </button>
    ))}
  </div>
)

// ── YearButtons ───────────────────────────────────────────────────────────────

const YEARS_VISIBLE = 4

const YearButtons = ({ years, activeYear, onSelect }) => {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? years : years.slice(0, YEARS_VISIBLE)
  const hidden  = years.length - YEARS_VISIBLE

  return (
    <div className='year-btns'>
      <button
        onClick={() => onSelect('')}
        className={`year-btn${activeYear === '' ? ' year-btn--active' : ''}`}
      >
        Усі роки
      </button>
      {visible.map(year => (
        <button
          key={year}
          onClick={() => onSelect(String(year))}
          className={`year-btn${activeYear === String(year) ? ' year-btn--active' : ''}`}
        >
          {year}
        </button>
      ))}
      {hidden > 0 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className='year-btn-more'
        >
          {expanded ? '▴ згорнути' : `▾ ще ${hidden}`}
        </button>
      )}
    </div>
  )
}

// ── SpeakerDropdown ───────────────────────────────────────────────────────────

const SpeakerDropdown = ({ people, activeSpeakerId, onSelect }) => {
  const [filterQuery, setFilterQuery] = useState('')
  const [open, setOpen]               = useState(false)
  const ref                           = useRef(null)

  const entries = Object.entries(people).sort((a, b) =>
    (a[1].nameUk || '').localeCompare(b[1].nameUk || '', 'uk')
  )

  const activeName = activeSpeakerId
    ? (people[activeSpeakerId]?.nameUk || activeSpeakerId)
    : ''

  const filtered = entries.filter(([, p]) =>
    !filterQuery || (p.nameUk || '').toLowerCase().includes(filterQuery.toLowerCase())
  )

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (id) => {
    onSelect(id)
    setFilterQuery('')
    setOpen(false)
  }

  return (
    <div ref={ref} className='speaker-dropdown'>
      <input
        type='text'
        value={open ? filterQuery : activeName}
        placeholder='Усі проповідники'
        onFocus={() => { setOpen(true); setFilterQuery('') }}
        onChange={e => setFilterQuery(e.target.value)}
        className={`speaker-dropdown__input${activeSpeakerId ? ' speaker-dropdown__input--filled' : ''}`}
      />
      <span
        className='arch-chevron'
        onClick={() => { if (activeSpeakerId) { handleSelect('') } else { setOpen(o => !o) } }}
      >
        {activeSpeakerId ? '✕' : '▾'}
      </span>

      {open && (
        <div className='speaker-dropdown__list'>
          <div
            className='speaker-dropdown__item speaker-dropdown__item--header'
            onMouseDown={() => handleSelect('')}
          >
            Усі проповідники
          </div>
          {filtered.length === 0 && (
            <div className='speaker-dropdown__empty'>Нічого не знайдено</div>
          )}
          {filtered.map(([id, person]) => (
            <div
              key={id}
              className='speaker-dropdown__item'
              onMouseDown={() => handleSelect(id)}
              style={id === activeSpeakerId ? { color: '#c0622a', fontWeight: '700', background: '#fdf6ee' } : {}}
            >
              {person.nameUk || person.nameEn || id}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── SearchInput ───────────────────────────────────────────────────────────────

const SearchInput = ({ value, onChange }) => (
  <input
    type='text'
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder='Пошук...'
    className='arch-search'
  />
)

// ── ArchiveEmpty ──────────────────────────────────────────────────────────────

const ArchiveEmpty = ({ onReset }) => (
  <div className='arch-empty'>
    <div className='arch-empty__icon'>📭</div>
    <p className='arch-empty__text'>Нічого не знайдено за вибраними фільтрами</p>
    <button onClick={onReset} className='arch-empty__reset'>Скинути фільтри</button>
  </div>
)

// ── WebArchive (main) ─────────────────────────────────────────────────────────

export const WebArchive = () => {
  const [videos, setVideos]               = useState([])
  const [people, setPeople]               = useState({})
  const [loading, setLoading]             = useState(true)
  const [filterCategory, setFilterCategory] = useState('')
  const [filterYear, setFilterYear]       = useState('')
  const [filterSpeakerId, setFilterSpeakerId] = useState('')
  const [searchTerm, setSearchTerm]       = useState('')

  // Load archivePeople once
  useEffect(() => {
    getDocs(collection(db, 'archivePeople'))
      .then(snap => {
        const map = {}
        snap.docs.forEach(d => { map[d.id] = d.data() })
        setPeople(map)
      })
      .catch(() => {})
  }, [])

  // Subscribe to archive collection — normalise legacy field names on the fly
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'archive'), orderBy('year', 'desc')),
      snap => {
        setVideos(snap.docs.map(d => {
          const raw = d.data()
          return {
            id: d.id,
            ...raw,
            titleUk:    raw.titleUk    || raw.title    || '',
            titleEn:    raw.titleEn    || '',
            category:   raw.category   || LEGACY_CATEGORY_MAP[raw.type] || raw.type || '',
            videoId:    raw.videoId    || extractVideoId(raw.link || ''),
            speakerIds: raw.speakerIds || [],
            _authorLegacy: raw.author  || '',
          }
        }))
        setLoading(false)
      },
      () => setLoading(false)
    )
    return unsub
  }, [])

  const availableYears = useMemo(
    () => [...new Set(videos.map(v => v.year))].filter(Boolean).sort((a, b) => b - a),
    [videos]
  )

  const allPeople = useMemo(() => {
    const legacyMap = {}
    videos.forEach(v => {
      if (v._authorLegacy) legacyMap[v._authorLegacy] = { nameUk: v._authorLegacy }
    })
    return { ...legacyMap, ...people }
  }, [videos, people])

  const filtered = useMemo(() => {
    return videos.filter(v => {
      if (filterCategory && v.category !== filterCategory) return false
      if (filterYear && v.year !== Number(filterYear)) return false
      if (filterSpeakerId) {
        const inNew = (v.speakerIds || []).includes(filterSpeakerId)
        const inLegacy = v._authorLegacy === filterSpeakerId
        if (!inNew && !inLegacy) return false
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase()
        const speakerMatch =
          (v._authorLegacy || '').toLowerCase().includes(q) ||
          (v.speakerIds || []).some(id =>
            (people[id]?.nameUk || '').toLowerCase().includes(q) ||
            (people[id]?.nameEn || '').toLowerCase().includes(q)
          )
        return (
          (v.titleUk || '').toLowerCase().includes(q) ||
          (v.titleEn || '').toLowerCase().includes(q) ||
          (v.bible || '').toLowerCase().includes(q) ||
          (v.keywords || []).some(k => k.toLowerCase().includes(q)) ||
          speakerMatch
        )
      }
      return true
    }).sort((a, b) => {
      const da = a.date?.toDate?.() ?? new Date(a.year || 0, 0, 1)
      const db = b.date?.toDate?.() ?? new Date(b.year || 0, 0, 1)
      return db - da
    })
  }, [videos, people, filterCategory, filterYear, filterSpeakerId, searchTerm])

  const handleReset = () => {
    setFilterCategory('')
    setFilterYear('')
    setFilterSpeakerId('')
    setSearchTerm('')
  }

  return (
    <>
      <ArchiveHero />

      <div className='arch-page'>
        <div className='arch-filter-bar'>
          <CategoryPills activeCategory={filterCategory} onToggle={setFilterCategory} />
          <YearButtons years={availableYears} activeYear={filterYear} onSelect={setFilterYear} />
          <SpeakerDropdown
            people={allPeople}
            activeSpeakerId={filterSpeakerId}
            onSelect={setFilterSpeakerId}
          />
          <SearchInput value={searchTerm} onChange={setSearchTerm} />
        </div>

        {loading && <p className='arch-loading'>•••</p>}

        {!loading && filtered.length === 0 && <ArchiveEmpty onReset={handleReset} />}

        {!loading && filtered.length > 0 && (
          <div className='arch-grid'>
            {filtered.map(video => {
              const speakerNames = (video.speakerIds || [])
                .map(id => people[id]?.nameUk || people[id]?.nameEn || null)
                .filter(Boolean)
              return (
                <VideoCard
                  key={video.id}
                  video={video}
                  speakerNames={speakerNames}
                />
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
